import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lp } = useLang();
  const idx = t.services.findIndex((s) => s.slug === slug);
  const s = t.services[idx];
  useSeo("services", s ? [`${s.title} | Atlas Bridge Logistics`, s.short] : undefined);
  if (!s) return <Navigate to={lp("/services")} replace />;
  const next = t.services[(idx + 1) % t.services.length];

  return (
    <>
      <PageHero eyebrow={t.nav.services} title={s.title} intro={s.intro} img={s.img} testId="service-hero">
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to={lp(`/quote?service=${s.slug}`)} className="btn-brass justify-center" data-testid="service-hero-quote-button">{t.common.requestQuote}<ArrowRight className="h-4 w-4 rtl:-scale-x-100" /></Link>
          <Link to={lp("/services")} className="btn-ghost-light justify-center" data-testid="service-back-link"><ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />{t.common.back}</Link>
        </div>
      </PageHero>
      <section className="bg-white py-24 lg:py-32" data-testid={`service-detail-${s.slug}`}>
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-7">
            <Eyebrow>{t.common.included}</Eyebrow>
            <ol className="mt-8 border-t border-[#8EA3BC]/40" data-testid="service-included-list">
              {s.included.map((item, i) => (
                <Reveal as="li" key={item} delay={i * 0.05} className="grid grid-cols-[3rem,1fr] gap-4 border-b border-[#8EA3BC]/40 py-6">
                  <span className="font-serif text-lg text-[#A27D17]" dir="ltr">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg leading-relaxed text-[#1E293B]">{item}</span>
                </Reveal>
              ))}
            </ol>
            {s.note && (
              <p className="mt-8 flex gap-3 border-s-2 border-[#C59B27] bg-[#F7F9FB] p-5 text-[#1E3A5F]" data-testid="service-note">
                <Info className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />{s.note}
              </p>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute bottom-0 start-0 h-1 w-24 bg-[#C59B27]" aria-hidden="true" />
              </div>
              <div className="border border-t-0 border-[#8EA3BC]/40 p-8">
                <Eyebrow>{t.common.whoFor}</Eyebrow>
                <p className="mt-4 font-serif text-xl leading-snug text-[#1E3A5F]" data-testid="service-who-for">{s.forWho}</p>
                <p className="mt-6 text-sm text-[#4A5D75]">{t.common.noPrices}</p>
                <Link to={lp(`/quote?service=${s.slug}`)} className="btn-brass mt-6 w-full justify-center" data-testid="service-quote-button">{t.common.requestQuote}</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Link to={lp(`/services/${next.slug}`)} className="group block border-t border-[#8EA3BC]/40 bg-[#F7F9FB] py-14" data-testid="service-next-link">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
          <span className="font-serif text-2xl text-[#1E3A5F] sm:text-4xl">{next.title}</span>
          <ArrowRight className="h-8 w-8 text-[#1E3A5F] transition-transform duration-300 group-hover:translate-x-2 rtl:-scale-x-100 rtl:group-hover:-translate-x-2" />
        </div>
      </Link>
    </>
  );
}
